import Vue, { CreateElement } from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'

import { Button } from '@src/elements/button/button'
import ApiClient from '@src/lib/api_client'
import { HealthGoal, HealthIndicator, HealthIndicatorSourceType, HealthIndicatorType } from '@src/data/goal'
import { Input } from '@src/elements/input/input'
import { OptionGroup } from '@src/elements/select/option-group'
import { Select } from '@src/elements/select/select'
import { Option } from '@src/elements/select/option'
import Activity from '@src/data/activity'
import { API } from '@src/lib/api_path'
import { validateHealthIndicator } from '@src/util/validation'
import { snakecaseKeys } from '@src/util/functions'
import { Divider } from '@src/elements/divider/divider'
import EventBus from '@src/lib/event_bus'

import './add-kgi.sass'

type HealthGoalResponse = { health_goal: HealthGoal }

@Component({
  computed: {
    activities () {
      return this.$store.state.activities || []
    },
    exerciseTags () {
      return this.$store.state.completeExerciseTags || []
    },
    goal () {
      return this.$store.state.goal
    }
  }
})
export default class AddKgi extends Vue {

  @Prop({ required: false, default: '' })
  private option: string

  @Prop({ required: false })
  private saveCallback: () => void

  private apiClient: ApiClient

  private kgis: HealthIndicator[] = []

  private activities: Activity[]
  private exerciseTags: any

  private selectedOption: { id: number, key: string } = { id: 0, key: '' }
  private sources: Set<{ id: number, key: string }> = new Set([])

  private fieldErrors: string[] = []

  private goal: HealthGoal

  constructor () {
    super()

    this.apiClient = new ApiClient('')
  }

  save () {
    if (this.kgis.length > 0) {
      if (!this.goal) {
        this.$store.dispatch('showToast', { message: this.$t('PAGE.MEMBERS.GOALS.SAVE_GOAL_FIRST'), fadeOut: true })
        return undefined
      }
      this.validate()
      if (this.fieldErrors.length === 0) {

        // add new KGIs to the current goal (add only KGIs with target value set and ignore the ones with 0)
        return Promise.all([this.kgis.filter(kgi => kgi.target && kgi.target > 0).map(kgi => {
          // check if it's an add or edit operation
          const foundKgi = this.goal.indicators.filter(indicator => indicator.type === kgi.type && (indicator.sourceId ? indicator.sourceId === kgi.sourceId : true))[0]
          if (foundKgi) {
            return this.editIndicator(kgi, foundKgi.id)
          } else {
            return this.addIndicator(kgi)
          }
        })])
          .then(() => {
            if (this.saveCallback) {
              this.saveCallback()
            }
            EventBus.trigger('reloadHealthGoal')
            EventBus.trigger('reloadSelectedMember')
            this.loadCurrentGoal()
          })
      }
    }
  }

  addIndicator (kgi: HealthIndicator) {
    return this.apiClient.post(API.TRAINERS_API.HEALTH_GOAL_INDICATORS.replace('{memberId}', this.$route.params.id), { indicator: snakecaseKeys(kgi) })
      .catch((error) => console.log('cannot save new health indicator', error))
  }

  editIndicator (kgi: HealthIndicator, kgiId: number) {
    // format the data to be sent to server
    if (!kgi.sourceId || !kgi.sourceType) {
      delete (kgi as any).sourceId
      delete (kgi as any).sourceType
    }

    return this.apiClient.put(`${API.TRAINERS_API.HEALTH_GOAL_INDICATORS.replace('{memberId}', this.$route.params.id)}/${kgiId}`, { indicator: snakecaseKeys(kgi) })
      .catch((error) => console.log('cannot edit health indicator', error))
  }

  loadCurrentGoal () {
    setTimeout(() => {
      return this.apiClient.get(API.TRAINERS_API.HEALTH_GOAL.replace('{memberId}', this.$route.params.id))
        .then((response: HealthGoalResponse) => {
          let goal = new HealthGoal(camelcaseKeys(response.health_goal, { deep: true }))
          goal.indicators = response.health_goal.indicators.map(indicator => new HealthIndicator(camelcaseKeys(indicator, { deep: true })))
          this.$store.dispatch('setGoal', goal)
        })
        .catch(error => console.log('cannot load health goal', error))
    }, 500)
  }

  validate () {
    let fieldErrors: string[] = []
    this.kgis.forEach(indicator => {
      fieldErrors.push(...validateHealthIndicator(indicator))
    })
    this.fieldErrors = fieldErrors
  }

  setIndicatorTargetValue (targetValue, type: HealthIndicatorType, sourceId?: number) {
    if (!targetValue) return
    // add new KGI if it does not already exist
    let indicator = this.kgis.filter(indicator => indicator.type === type && indicator.sourceId === sourceId)[0]
    if (!indicator) {
      // build the source type based on the indicator type
      const sourceType = type === HealthIndicatorType.ACTIVITY_FREQUENCY || type === HealthIndicatorType.ACTIVITY_DURATION ? HealthIndicatorSourceType.ACTIVITY
        : type === HealthIndicatorType.EXERCISE_TAG_PERCENTAGE ? HealthIndicatorSourceType.EXERCISETAG : null
      // create the kgi
      let newIndicator = new HealthIndicator({ type: type, target: targetValue, sourceId: sourceId })
      if (sourceType) {
        newIndicator.sourceType = sourceType
      }
      this.kgis.push(newIndicator)

    } else {
      // update the existing kgi
      indicator.target = targetValue
    }
  }

  addSelectedOption () {
    this.sources.add(this.selectedOption)
    this.$forceUpdate()
  }

  render (h: CreateElement) {
    let body = this.renderKgiForm(this.option, h)

    if (body) {
      return <div class='kgi-form-wrapper'>
        <span class='kgi-title'>{this.$t(`PAGE.MEMBERS.GOALS.KGI.${this.option.toUpperCase()}`)}</span>
        <div class='kgi-form'>{body}</div>
        <Button label={this.$t('SAVE')} class='save-btn' ref='saveBtn' click={this.save} disabled={!this.kgis.length} />
      </div>
    }
  }

  /* Each KGI has a different form format */
  renderKgiForm (option: string, h: CreateElement) {
    switch (option) {
      case 'ACTIVITY':
        return this.renderActivitiesForm(h)
      case 'STATION':
      case 'HOME':
        return this.renderWorkoutsForm(h, option)
      case 'MET':
        return this.renderMetForm(h)
      case 'PIX_SCORE':
        return this.renderPixScoreForm(h)
      case 'BODY_AREA':
        return this.renderExerciseTagsForm(h, 'BODY_AREA', this.exerciseTags.filter(tag => tag.type === 'ExerciseTags::BodyArea'))
      case 'MUSCLE_GROUP':
        return this.renderExerciseTagsForm(h, 'MUSCLE_GROUP', this.exerciseTags.filter(tag => tag.type === 'ExerciseTags::MuscleGroup'))
      case 'TRAINING_FOCUS':
        return this.renderExerciseTagsForm(h, 'TRAINING_FOCUS', this.exerciseTags.filter(tag => tag.type === 'ExerciseTags::TrainingFocus'))
      default:
        console.log('unknown kgi type')
        return null
    }
  }

  /* Input for value - MET */
  renderMetForm (h: CreateElement) {
    return <Input type='number' value={this.kgis[0] ? this.kgis[0].target : null} class={`kgi-input wide ${this.fieldErrors.includes('value') ? 'error' : ''}`}
                  valueChanged={value => this.setIndicatorTargetValue(value, HealthIndicatorType.MET)} ref='valueInput' label={this.$t('PAGE.MEMBERS.GOALS.KGI_INPUT.MET_WEEK')} />
  }

  /* Input for value - PIX score */
  renderPixScoreForm (h: CreateElement) {
    return <Input type='number' value={this.kgis[0] ? this.kgis[0].target : null} class={`kgi-input wide ${this.fieldErrors.includes('value') ? 'error' : ''}`}
                  valueChanged={value => this.setIndicatorTargetValue(value, HealthIndicatorType.PIX_SCORE)} ref='valueInput' label={this.$t('PAGE.MEMBERS.GOALS.KGI_INPUT.PER_WEEK')} />
  }

  /* 2 inputs for duration and frequency */
  renderWorkoutsForm (h: CreateElement, option: string) {
    const durationKGI = this.kgis.filter(indicator => indicator.type === HealthIndicatorType[`${option}_WORKOUT_DURATION`])[0]
    const duration = durationKGI ? durationKGI.target : null
    const frequencyKGI = this.kgis.filter(indicator => indicator.type === HealthIndicatorType[`${option}_WORKOUT_FREQUENCY`])[0]
    const frequency = frequencyKGI ? frequencyKGI.target : null
    return <div class='row'>
      <Input type='number' value={duration} class='kgi-input' label={this.$t('MINUTES')}
             valueChanged={value => this.setIndicatorTargetValue(value, HealthIndicatorType[`${option}_WORKOUT_DURATION`])} ref='durationInput' />
      <Input type='number' value={frequency} class='kgi-input' label={this.$t('PAGE.MEMBERS.GOALS.KGI_INPUT.TIMES_WEEK')}
             valueChanged={value => this.setIndicatorTargetValue(value, HealthIndicatorType[`${option}_WORKOUT_FREQUENCY`])} ref='frequencyInput' />
    </div>
  }

  /* Dropdown to select the exercise tag and an input for the percentage value */
  renderExerciseTagsForm (h: CreateElement, option: string, exerciseTags) {
    // display all the activities in a dropdown
    let tags = exerciseTags.map(tag => {
      return <Option type='single-option' value={tag.id.toString()} label={this.$t(`PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.${tag.key.replace('/', '_').toUpperCase()}`)}
                     selected={(this.selectedOption && this.selectedOption.id === tag.id)}
                     select={() => this.selectedOption = { id: tag.id, key: tag.key }} />
    })

    let index = 1
    let indicators: any[] = []

    // display a new entry for each selected exercise tag type
    this.sources.forEach(tag => {
      // get the corresponding percentage indicators, by exercise tag id
      const kgi = this.kgis.filter(indicator => (indicator.type === HealthIndicatorType.EXERCISE_TAG_PERCENTAGE) && indicator.sourceId === tag.id)[0]
      const target = kgi ? kgi.target : null

      indicators.push(<div>
        {`${index++}. ${this.$t(`PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.${tag.key.replace('/', '_').toUpperCase()}`)}`}<br />
        <Input type='number' value={target} class='kgi-input wide' label={this.$t('PAGE.MEMBERS.GOALS.KGI_INPUT.PERCENT_PER_WEEK')}
               valueChanged={value => this.setIndicatorTargetValue(value, HealthIndicatorType.EXERCISE_TAG_PERCENTAGE, tag.id)} ref='valueInput' />
      </div>)
    })

    return <div>
      <Select label={this.$t('CHOOSE_FROM_LIST')}
              value={this.selectedOption.key ? this.$t(`PAGE.CONTENT.WORKOUT.EXERCISE_TAGS.${this.selectedOption.key.replace('/', '_').toUpperCase()}`) : ''}>
        <OptionGroup>{tags}</OptionGroup>
      </Select>
      <Button label={this.$t('ADD')} click={this.addSelectedOption} class='add-btn' ref='addBtn' disabled={!this.selectedOption.key} />
      <Divider />
      {indicators.length > 0 ? <div class='selected-keys'>{indicators}</div> : null}
    </div>
  }

  /* Dropdown to select the activity and 2 inputs for duration and frequency */
  renderActivitiesForm (h: CreateElement) {
    // display all the activities in a dropdown
    let activities = this.activities.map(activity => {
      return <Option type='single-option' value={activity.id.toString()} label={this.$t(`ACTIVITIES.${activity.key.toUpperCase()}`)}
                     selected={(this.selectedOption && this.selectedOption.id === activity.id)}
                     select={() => this.selectedOption = { id: activity.id, key: activity.key }} />
    })

    let index = 1
    let indicators: any[] = []

    // display a new entry for each selected activity type
    this.sources.forEach(activity => {
      // get the corresponding duration and frequency activity indicators, by activity id
      const durationKGI = this.kgis.filter(indicator => (indicator.type === HealthIndicatorType.ACTIVITY_DURATION) && indicator.sourceId === activity.id)[0]
      const duration = durationKGI ? durationKGI.target : null
      const frequencyKGI = this.kgis.filter(indicator => (indicator.type === HealthIndicatorType.ACTIVITY_FREQUENCY) && indicator.sourceId === activity.id)[0]
      const frequency = frequencyKGI ? frequencyKGI.target : null

      indicators.push(<div>
        {`${index++}. ${this.$t(`ACTIVITIES.${activity.key.toUpperCase()}`)}`}<br />
        <div class='row'>
          <Input type='number' value={duration} class='kgi-input' label={this.$t('MINUTES')}
                 valueChanged={value => this.setIndicatorTargetValue(value, HealthIndicatorType.ACTIVITY_DURATION, activity.id)} ref='durationInput' />
          <Input type='number' value={frequency} class='kgi-input' label={this.$t('PAGE.MEMBERS.GOALS.KGI_INPUT.TIMES_WEEK')}
                 valueChanged={value => this.setIndicatorTargetValue(value, HealthIndicatorType.ACTIVITY_FREQUENCY, activity.id)} ref='frequencyInput' />
        </div>
      </div>)
    })
    return <div>
      <Select label={this.$t('CHOOSE_FROM_LIST')} value={this.selectedOption.key ? this.$t(`ACTIVITIES.${this.selectedOption.key.toUpperCase()}`) : ''}>
        <OptionGroup>{activities}</OptionGroup>
      </Select>
      <Button label={this.$t('ADD')} click={this.addSelectedOption} class='add-btn' ref='addBtn' disabled={!this.selectedOption.key}/>
      <Divider />
      {indicators.length > 0 ? <div class='selected-keys'>{indicators}</div> : null}
    </div>
  }
}
